import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Send, Mail, Phone, MapPin } from 'lucide-react';
import { requestHomeTop } from './ScrollToTop';

const helpLinks = [
  { label: 'Track Order', to: '/account/orders' },
  { label: 'Shipping Policy', to: '/shipping-policy' },
  { label: 'Returns & Refunds', to: '/refund-policy' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'About Us', to: '#story' },
];

const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Service', to: '/terms' },
];

export default function Footer() {
  return (
    <footer className="bg-[#1A2332] text-[#F5EFE6] mt-10">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
        <div className="sm:col-span-2 md:col-span-1">
          <Link to="/" onClick={() => requestHomeTop({ immediate: window.location.pathname === '/' })} className="inline-block">
            <div className="font-serif-display text-2xl tracking-[0.2em] sm:tracking-[0.25em]">RAJPUROHIT</div>
            <div className="text-[11px] tracking-[0.45em] sm:tracking-[0.55em] opacity-70 mt-1">PERFUMES</div>
          </Link>
          <p className="text-sm text-[#F5EFE6]/70 mt-5 max-w-xs leading-relaxed">
            India’s inspired perfume house. Long-lasting fragrances, crafted for the everyday and the extraordinary.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href="https://www.instagram.com/rajpurohitperfumes?igsh=MWpkOTQ3Mm1pcWM2bg==" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#F5EFE6]/30 flex items-center justify-center hover:bg-[#F5EFE6] hover:text-[#1A2332] transition touch-manipulation" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="w-10 h-10 rounded-full border border-[#F5EFE6]/30 flex items-center justify-center hover:bg-[#F5EFE6] hover:text-[#1A2332] transition touch-manipulation" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
            <a href="#" className="w-10 h-10 rounded-full border border-[#F5EFE6]/30 flex items-center justify-center hover:bg-[#F5EFE6] hover:text-[#1A2332] transition touch-manipulation" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
          </div>
        </div>

        <div>
          <h4 className="text-[11px] tracking-[0.3em] uppercase opacity-90 mb-4">Shop</h4>
          <ul className="space-y-2 text-sm text-[#F5EFE6]/70">
            {['Shop All', 'For Him', 'For Her', 'Unisex', 'Sample Kit', 'Bundles', 'New Arrivals'].map(l => (
              <li key={l}><a href="#" className="hover:text-[#F5EFE6] transition py-0.5 inline-block touch-manipulation">{l}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-[11px] tracking-[0.3em] uppercase opacity-90 mb-4">Help</h4>
          <ul className="space-y-2 text-sm text-[#F5EFE6]/70">
            {helpLinks.map((l) => (
              <li key={l.label}>
                {l.to.startsWith('/') ? (
                  <Link to={l.to} className="hover:text-[#F5EFE6] transition py-0.5 inline-block touch-manipulation">
                    {l.label}
                  </Link>
                ) : (
                  <a href={l.to} className="hover:text-[#F5EFE6] transition py-0.5 inline-block touch-manipulation">
                    {l.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-5 space-y-2 text-sm text-[#F5EFE6]/70">
            <a href="tel:+918866655699" className="flex items-center gap-2 hover:text-[#F5EFE6] transition">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" /> +91 88666 55699
            </a>
            <a href="mailto:rajpurohitperfumes@gmail.com" className="flex items-center gap-2 break-all hover:text-[#F5EFE6] transition">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" /> rajpurohitperfumes@gmail.com
            </a>
            <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-1 flex-shrink-0" /> Vadodara, Gujarat, India</div>
          </div>
        </div>

        <div className="sm:col-span-2 md:col-span-1">
          <h4 className="text-[11px] tracking-[0.3em] uppercase opacity-90 mb-4">Join The House</h4>
          <p className="text-sm text-[#F5EFE6]/70 mb-4">Get 10% off your first order and early access to new launches.</p>
          <form className="flex items-center border border-[#F5EFE6]/30 rounded-sm overflow-hidden" onSubmit={e => { e.preventDefault(); e.target.reset(); }}>
            <input type="email" placeholder="Your email" className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm outline-none placeholder-[#F5EFE6]/50" />
            <button type="submit" className="px-3 py-2.5 bg-[#F5EFE6] text-[#1A2332] hover:bg-[#C9A961] transition flex-shrink-0 touch-manipulation" aria-label="Subscribe"><Send className="w-4 h-4" /></button>
          </form>
          <p className="text-[10px] text-[#F5EFE6]/40 mt-3 tracking-wide">
            By subscribing you agree to our{' '}
            <Link to="/privacy" className="underline hover:text-[#F5EFE6]/70">privacy policy</Link>.
          </p>
        </div>
      </div>
      <div className="border-t border-[#F5EFE6]/10 py-5 px-4 text-center text-[11px] sm:text-xs text-[#F5EFE6]/50">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
          {legalLinks.map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-[#F5EFE6] transition touch-manipulation">
              {l.label}
            </Link>
          ))}
        </div>
        © {new Date().getFullYear()} Rajpurohit Perfumes. All Rights Reserved. • Handcrafted in India.
      </div>
    </footer>
  );
}
